import React from "react";

const RoadmapConfiguration = ({ data, onChange, onNext, onBack }) => {
  const configuration = data.configuration || {};

  const handleConfigChange = (key, value) => {
    onChange({
      configuration: {
        ...configuration,
        [key]: value,
      },
    });
  };

  return (
    <div>
      <h2 className="text-xl font-semibold text-white mb-2">
        Roadmap Configuration
      </h2>
      <p className="text-gray-400 mb-6">
        Configure timeline, clustering, and display options for your roadmap
        report
      </p>

      <div className="space-y-6">
        {/* Timeline Settings */}
        <section>
          <h3 className="text-lg font-medium text-white mb-4">
            Timeline Settings
          </h3>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Default Zoom Level
              </label>
              <select
                value={configuration.defaultZoom || "year"}
                onChange={(e) =>
                  handleConfigChange("defaultZoom", e.target.value)
                }
                className="w-full px-4 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-blue-500"
              >
                <option value="year">Year</option>
                <option value="quarter">Quarter</option>
                <option value="month">Month</option>
              </select>
              <p className="text-xs text-gray-500 mt-1">
                Users can change zoom level when viewing the report
              </p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Start Date Attribute
                </label>
                <select
                  value={configuration.startDateAttribute || "plan_start_date"}
                  onChange={(e) =>
                    handleConfigChange("startDateAttribute", e.target.value)
                  }
                  className="w-full px-4 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-blue-500"
                >
                  <option value="plan_start_date">Plan Start Date</option>
                  <option value="phase_in_start_date">
                    Phase In Start Date
                  </option>
                  <option value="active_start_date">Active Start Date</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  End Date Attribute
                </label>
                <select
                  value={configuration.endDateAttribute || "eol_start_date"}
                  onChange={(e) =>
                    handleConfigChange("endDateAttribute", e.target.value)
                  }
                  className="w-full px-4 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-blue-500"
                >
                  <option value="phase_out_start_date">
                    Phase Out Start Date
                  </option>
                  <option value="eol_start_date">End of Life Start Date</option>
                </select>
              </div>
            </div>
          </div>
        </section>

        {/* Clustering Settings */}
        <section>
          <h3 className="text-lg font-medium text-white mb-4">
            Clustering Options
          </h3>
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                id="enableClustering"
                checked={configuration.enableClustering !== false}
                onChange={(e) =>
                  handleConfigChange("enableClustering", e.target.checked)
                }
                className="w-4 h-4 text-blue-600 bg-gray-900 border-gray-700 rounded focus:ring-blue-500"
              />
              <label
                htmlFor="enableClustering"
                className="text-sm text-gray-300"
              >
                Enable clustering/grouping
              </label>
            </div>

            {configuration.enableClustering !== false && (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Cluster By Entity Types
                  </label>
                  <div className="space-y-2">
                    {[
                      "business-capability",
                      "organization",
                      "business-context",
                      "initiative",
                    ].map((type) => (
                      <label
                        key={type}
                        className="flex items-center gap-2 text-sm text-gray-300"
                      >
                        <input
                          type="checkbox"
                          checked={(
                            configuration.clusterByTypes || []
                          ).includes(type)}
                          onChange={(e) => {
                            const current = configuration.clusterByTypes || [];
                            const updated = e.target.checked
                              ? [...current, type]
                              : current.filter((t) => t !== type);
                            handleConfigChange("clusterByTypes", updated);
                          }}
                          className="w-4 h-4 text-blue-600 bg-gray-900 border-gray-700 rounded focus:ring-blue-500"
                        />
                        <span className="capitalize">
                          {type.replace("-", " ")}
                        </span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Drill-down configuration for business capability clustering */}
                {(configuration.clusterByTypes || []).includes(
                  "business-capability"
                ) && (
                  <div className="mt-4 p-4 bg-gray-800 rounded-lg border border-gray-700">
                    <h4 className="text-sm font-semibold text-white mb-3">
                      Business Capability Hierarchy Options
                    </h4>

                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                          Drill Down To Entity Type
                        </label>
                        <select
                          value={
                            configuration.capabilityDrillDownType ||
                            "application"
                          }
                          onChange={(e) =>
                            handleConfigChange(
                              "capabilityDrillDownType",
                              e.target.value
                            )
                          }
                          className="w-full px-4 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-blue-500"
                        >
                          <option value="application">Application</option>
                          <option value="interface">Interface</option>
                          <option value="technology-component">
                            Technology Component
                          </option>
                          <option value="data-object">Data Object</option>
                          <option value="initiative">Initiative</option>
                        </select>
                        <p className="text-xs text-gray-500 mt-1">
                          Show entities of this type under each capability in
                          the hierarchy
                        </p>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                          Capability Level Filter
                        </label>
                        <div className="space-y-2">
                          <p className="text-xs text-gray-400 mb-2">
                            Select which capability levels to include (1 = top
                            level, 2 = second level, etc.)
                          </p>
                          <div className="flex flex-wrap gap-2">
                            {[1, 2, 3, 4, 5].map((level) => (
                              <label
                                key={level}
                                className="flex items-center gap-2 text-sm text-gray-300 px-3 py-1 bg-gray-900 rounded border border-gray-700"
                              >
                                <input
                                  type="checkbox"
                                  checked={(
                                    configuration.capabilityLevels || [
                                      1, 2, 3, 4, 5,
                                    ]
                                  ).includes(level)}
                                  onChange={(e) => {
                                    const current =
                                      configuration.capabilityLevels || [
                                        1, 2, 3, 4, 5,
                                      ];
                                    const updated = e.target.checked
                                      ? [...current, level]
                                          .filter(
                                            (l, i, arr) => arr.indexOf(l) === i
                                          )
                                          .sort()
                                      : current.filter((l) => l !== level);
                                    handleConfigChange(
                                      "capabilityLevels",
                                      updated
                                    );
                                  }}
                                  className="w-4 h-4 text-blue-600 bg-gray-900 border-gray-700 rounded focus:ring-blue-500"
                                />
                                <span>Level {level}</span>
                              </label>
                            ))}
                          </div>
                          <p className="text-xs text-gray-500 mt-1">
                            Level 1 = Root capabilities, Level 2 = Direct
                            children, etc.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </section>

        {/* Display Options */}
        <section>
          <h3 className="text-lg font-medium text-white mb-4">
            Display Options
          </h3>
          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                id="showSuccessors"
                checked={configuration.showSuccessors !== false}
                onChange={(e) =>
                  handleConfigChange("showSuccessors", e.target.checked)
                }
                className="w-4 h-4 text-blue-600 bg-gray-900 border-gray-700 rounded focus:ring-blue-500"
              />
              <label htmlFor="showSuccessors" className="text-sm text-gray-300">
                Show successor relationships
              </label>
            </div>

            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                id="showPredecessors"
                checked={configuration.showPredecessors === true}
                onChange={(e) =>
                  handleConfigChange("showPredecessors", e.target.checked)
                }
                className="w-4 h-4 text-blue-600 bg-gray-900 border-gray-700 rounded focus:ring-blue-500"
              />
              <label
                htmlFor="showPredecessors"
                className="text-sm text-gray-300"
              >
                Show predecessor relationships
              </label>
            </div>
          </div>
        </section>
      </div>

      <div className="flex justify-between mt-8">
        <button
          onClick={onBack}
          className="px-6 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg font-semibold transition-colors"
        >
          Back
        </button>
        <button
          onClick={onNext}
          className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-semibold transition-colors"
        >
          Next: Preview
        </button>
      </div>
    </div>
  );
};

export default RoadmapConfiguration;
